import React, { useEffect, useMemo, useState } from "react";
import { View, Text, TouchableOpacity, StyleSheet, FlatList, Platform } from "react-native";
import { useTranslation } from "react-i18next";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import { billerSearch } from "../../../../../services/billpay.service";
import FloatingTextInputComponent from "../../../common/FloatingTextInputComponent";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import Colors from "../../../../../constants/colors";
import RadioSelectedIcon from "../../../../../../../../assets/images/RadioSelectButton.svg";
import { formatBillerDisplayName } from "../../../../../helper/billerDisplayName";

interface PreviosBillerInfoStepProps {
  onSelectBiller?: (biller: any) => void;
  testID?: string;
}

function billerKeyFromRaw(raw: any, fallbackIdx = 0): string | number {
  if (raw == null) return fallbackIdx;
  return raw.BillerId ?? raw.billerId ?? raw.Id ?? raw.id ?? fallbackIdx;
}

function rowFromBillerRaw(raw: any): { key: string | number; name: string; raw: any } {
  const key = billerKeyFromRaw(raw);
  const name = String(raw?.BillerName ?? raw?.billerName ?? raw?.name ?? key);
  return { key, name, raw };
}

const PreviosBillerInfoStep: React.FC<PreviosBillerInfoStepProps> = ({ onSelectBiller, testID }) => {
  const { t } = useTranslation();
  const billPayAuth = useSessionStore(
    (s: SessionState) => (s as any).billPayGetAuthConsumerResponseStore
  );
  const agentId = useSessionStore((s: SessionState) => s.agentId);
  const unitProfileId = useSessionStore((s: SessionState) => s.unitProfileId);
  const setSelectedBillerInformationStore = useSessionStore((s: SessionState) => (s as any).setSelectedBillerInformationStore);
  const setBillPayBillerAccountNumberStore = useSessionStore((s: SessionState) => (s as any).setBillPayBillerAccountNumberStore);
  const setBillPayBillerSearchResponseStore = useSessionStore((s: SessionState) => (s as any).setBillPayBillerSearchResponseStore);
  const selectedBillerFromStore = useSessionStore((s: SessionState) => (s as any).selectedBillerInformationStore);
  const [query, setQuery] = useState(() => {
    const raw = (useSessionStore.getState() as any).selectedBillerInformationStore;
    if (!raw) return "";
    return String(raw.BillerName ?? raw.billerName ?? raw.name ?? "");
  });
  const [selectedKey, setSelectedKey] = useState<string | number | null>(() => {
    const raw = (useSessionStore.getState() as any).selectedBillerInformationStore;
    return raw ? billerKeyFromRaw(raw) : null;
  });
  const [searchResultRaw, setSearchResultRaw] = useState<any | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const allBillers: any[] = useMemo(() => {
    const src = (billPayAuth?.ConsumerInfo?.BillerLookupInfos) as any;
    if (Array.isArray(src)) return src;
    if (src && Array.isArray(src.BillerLookupInfos)) return src.BillerLookupInfos;
    return [];
  }, [billPayAuth]);

  // Normalize previous billers from auth
  const normalizedBillers = useMemo(() => {
    return allBillers.map((item, idx) => {
      const id = item?.BillerId ?? item?.billerId ?? item?.Id ?? item?.id ?? idx;
      const name = item?.BillerName ?? item?.billerName ?? String(item?.name ?? id);
      return { key: id, name, raw: item };
    });
  }, [allBillers]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return normalizedBillers;
    return normalizedBillers.filter(b => b.name?.toLowerCase().includes(q));
  }, [normalizedBillers, query]);

  // Fire billerSearch when query length >= 2
  useEffect(() => {
    let cancelled = false;
    const q = query.trim();
    if (q.length >= 2 && agentId && unitProfileId) {
      setSearchLoading(true);
      setSearchError(null);
      // debounce minimal via setTimeout
      const debounceTimer = setTimeout(() => {
        billerSearch(q, String(agentId), String(unitProfileId), 'NAME')
          .then((res) => {
            if (cancelled) return;
            setSearchResultRaw(res);
            try { setBillPayBillerSearchResponseStore?.(res); } catch {}
          })
          .catch((err) => {
            if (cancelled) return;
            setSearchError(err?.message || t("BILL_PAY_BILLER_SEARCH_FAILED"));
            setSearchResultRaw(null);
          })
          .finally(() => { if (!cancelled) setSearchLoading(false); });
      }, 350);
      return () => { cancelled = true; clearTimeout(debounceTimer); };
    } else {
      setSearchResultRaw(null);
      setSearchLoading(false);
      setSearchError(null);
    }
    return () => { cancelled = true; };
  }, [query, agentId, unitProfileId, t]);

  useEffect(() => {
    const raw = selectedBillerFromStore;
    setSelectedKey(raw ? billerKeyFromRaw(raw) : null);
    if (raw) {
      const name = String(raw.BillerName ?? raw.billerName ?? raw.name ?? "");
      if (name) setQuery(name);
    }
  }, [selectedBillerFromStore]);

  // Normalize search results
  const normalizedSearchResults = useMemo(() => {
    const s: any = searchResultRaw as any;
    if (!s) return [] as Array<{ key: string | number; name: string; raw: any }>;
    const listCandidate: any = Array.isArray(s)
      ? s
      : (s.BillerInfos ?? s.billerInfos ?? s.BillerLookupInfos ?? s.billerLookupInfos ?? s.Items ?? s.items ?? s.Result ?? s.result ?? []);
    const list: any[] = Array.isArray(listCandidate)
      ? listCandidate
      : (Array.isArray(listCandidate?.Items) ? listCandidate.Items : []);
    return list.map((item: any, idx: number) => {
      const id = item?.BillerId ?? item?.billerId ?? item?.Id ?? item?.id ?? idx;
      const name = item?.BillerName ?? item?.billerName ?? String(item?.name ?? id);
      return { key: id, name, raw: item };
    });
  }, [searchResultRaw]);

  const handleSelect = (item: { key: string | number; name: string; raw: any }) => {
    setSelectedKey(item.key);
    try { setSelectedBillerInformationStore?.(item.raw); } catch {}
    // Determine receiveCode source
    const raw: any = item.raw || {};
    let receiveCode: string | undefined = undefined;
    // Previous billers from AuthConsumer/ConsumerLookup
    receiveCode = raw?.ReceiveCode ?? raw?.receiveCode ?? receiveCode;
    // If an account number is present on the selected biller, store it for prefill
    const acct = raw?.BillerAccountNumber ?? raw?.billerAccountNumber ?? null;
    try { setBillPayBillerAccountNumberStore?.(acct ? String(acct) : null); } catch {}
    // If no receiveCode yet and selection came from search results, still try same keys
    if (!receiveCode && searchResultRaw) {
      receiveCode = raw?.ReceiveCode ?? raw?.receiveCode;
    }
    // Fire CODE search to resolve full/current biller info context
    try {
      if (agentId && unitProfileId) {
        billerSearch(String(item.name || ''), String(agentId), String(unitProfileId), 'CODE', receiveCode)
          .then((res) => { try { 
            setBillPayBillerSearchResponseStore?.(res);
          } catch {} })
          .catch(() => {});
      }
    } catch {}
    setTimeout(() => {
      onSelectBiller?.(item.raw);
    }, 200);
  };

  const isSearchResults = query.trim().length >= 2;
  const listDataBase = isSearchResults ? normalizedSearchResults : filtered;

  /** When navigating back, the stored biller may not appear in Recent or API results — always show the current selection. */
  const listData = useMemo(() => {
    const base = listDataBase;
    const raw = selectedBillerFromStore;
    if (!raw) return base;
    const selKey = billerKeyFromRaw(raw);
    const exists = base.some((r) => String(r.key) === String(selKey));
    if (exists) return base;
    return [rowFromBillerRaw(raw), ...base];
  }, [listDataBase, selectedBillerFromStore]);

  const selectedBillerRawName = useMemo(() => {
    const raw = selectedBillerFromStore;
    if (!raw) return "";
    return String(raw.BillerName ?? raw.billerName ?? raw.name ?? "");
  }, [selectedBillerFromStore]);

  /** Formatted in the search field when showing the selected biller; raw `query` kept for filter/API. */
  const searchInputValue = useMemo(() => {
    const q = query.trim();
    if (!q) return "";
    if (
      selectedKey != null &&
      selectedBillerRawName &&
      q.toLowerCase() === selectedBillerRawName.trim().toLowerCase()
    ) {
      return formatBillerDisplayName(q);
    }
    return query;
  }, [query, selectedKey, selectedBillerRawName]);

  const sectionTitle = isSearchResults
    ? t("BILL_PAY_PREVIOUS_BILLER_SECTION_RESULTS")
    : t("RECENT");
  const hasList = listData.length > 0;
  const emptyMessage = isSearchResults
    ? searchLoading
      ? t("BILL_PAY_PREVIOUS_BILLER_SEARCHING")
      : searchError || t("BILL_PAY_PREVIOUS_BILLER_NO_RESULTS")
    : "";

  const renderRow = (item: { key: string | number; name: string; raw: any }, testID: string) => (
    <TouchableOpacity style={styles.row} onPress={() => handleSelect(item)} testID={testID}>
      <View style={styles.radioButton}>
        {selectedKey === item.key && (
          <RadioSelectedIcon width={20} height={20} />
        )}
      </View>
      <Text style={styles.rowText}>{formatBillerDisplayName(item.name)}</Text>
    </TouchableOpacity>
  );

  const listHeader = <Text style={styles.sectionHeader}>{sectionTitle}</Text>;

  return (
    <View style={styles.containerSticky} testID={testID}>
      <View style={styles.stickySection}>
        <Text style={styles.title}>{t("SEARCH_FOR_A_COMPANY_TO_MAKE_A_PAYMENT")}</Text>
        <FloatingTextInputComponent
          testID="sendPhoneNumber"
          label={t("COUNTRY_STEP_SEARCH_PLACEHOLDER")}
          value={searchInputValue}
          isAutoFocus={false}
          onChangeText={setQuery}
          keyboardType="default"
        />
      </View>
      {hasList ? (
        <FlatList
          data={listData}
          keyExtractor={(item) => String(item.key)}
          ListHeaderComponent={listHeader}
          renderItem={({ item }) =>
            renderRow(item, isSearchResults ? "billerItem" : "recentBillerItem")
          }
          ItemSeparatorComponent={isSearchResults ? undefined : () => <View style={styles.separator} />}
          contentContainerStyle={styles.listContentSticky}
          style={[
            styles.listSticky,
            Platform.OS === "web" && ({ overflow: "auto" } as any),
          ]}
          nestedScrollEnabled
          showsVerticalScrollIndicator
        />
      ) : (
        <View style={styles.emptyWrapper}>
          <Text style={styles.emptyText}>{emptyMessage}</Text>
        </View>
      )}
    </View>
  );
};

export default PreviosBillerInfoStep;

const styles = StyleSheet.create({
  containerSticky: {
    flex: 1,
    minHeight: 0,
    paddingHorizontal: 24,
    backgroundColor: "#FFF",
  },
  stickySection: {
    flexShrink: 0,
  },
  listSticky: {
    flex: 1,
    width: "100%",
    minHeight: 0,
  },
  listContentSticky: {
    paddingBottom: Platform.OS === "web"
      ? ("max(24px, env(safe-area-inset-bottom, 0px))" as any)
      : 32,
  },
  emptyWrapper: {
    flex: 1,
    marginTop: 28,
    justifyContent: "flex-start",
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 28,
    marginTop: 24
  },
  sectionHeader: {
    marginTop: 28,
    fontFamily: ComponentStyles.textFamily,
    fontSize: 20,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 26,
    marginBottom: 8
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 16
  },
  rowText: {
    marginLeft: 16,
    fontFamily: ComponentStyles.textFamily,
    fontSize: 16,
    fontWeight: '400', 
    color: Colors.black,
    lineHeight: 24,
  },
  separator: {
    height: 1,
    backgroundColor: "#E5E7EB"
  },
  emptyText: {
    fontSize: 14,
    fontFamily: 'Rubik',
    color: "#6B7280"
  },
  radioButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.radioButtonFilled
  },
});


